import { type BoardBoardSource as BoardBoardSourceType, type BoardBoardSource } from '@gorilla/portal-shared/src/schemas';
import { Board, MoveArrowRight, Team } from '@vibe/icons';
import { Box, Flex, Icon, Divider } from '@vibe/core';
import { useSingleBoard } from '../../shared/hooks/use-boards';
import { extractBoardNameAndErrorMessage, EditBoardField, EditBoardSourceNameField, BoardSourceExplainer } from './common';
import { ExternalLink } from '../ExternalLink';

type BoardBoardSourceProps = {
  source: Partial<BoardBoardSourceType>;
  onChange: (source: Partial<BoardBoardSourceType>) => void;
  mode: 'create' | 'edit';
};

export function BoardBoardSource({ source, onChange }: BoardBoardSourceProps) {
  const singleBoardResult = useSingleBoard(source.boardId);
  const { boardName, boardErrorMessage } = extractBoardNameAndErrorMessage(singleBoardResult, source.boardId);

  return (
    <>
      <Box
        rounded="small"
        border
        borderColor="uiBorderColor"
        style={{
          margin: '0 0 24px 0',
        }}
      >
        <Box>
          <EditBoardField
            isCompleted={!!source.boardId}
            headline="Board"
            helpText="This is the board where all clients see the same items."
            introText="Choose the board to share with all clients"
            value={boardName}
            errorMessage={boardErrorMessage}
            onChange={(newSelectedBoardId) => {
              onChange({ ...source, boardId: newSelectedBoardId });
            }}
          />
          <Divider withoutMargin />
          <EditBoardSourceNameField
            headline="Name"
            helpText="This helps you identify the setup across the app."
            introText="Give this setup a unique name"
            value={source.name || ''}
            isCompleted={!!source.name}
            boardSourceName={source.name || ''}
            onChange={(newBoardSourceName) => {
              onChange({ ...source, name: newBoardSourceName });
            }}
          />
        </Box>
      </Box>
    </>
  );
}

export function BoardBoardSourceExplainer() {
  return (
    <BoardSourceExplainer
      title="Clients see the same items in the client portal"
      description="Each client will see all items from this board."
      videoSrc="/Onboarding-Shared-Board-Compressed.mp4"
    />
  );
}

export function BoardBoardSourceIndicator({
  children,
  helpLink,
  hideHelpLink,
}: {
  children: React.ReactNode;
  helpLink?: string;
  hideHelpLink?: boolean;
}) {
  return (
    <Flex gap={2}>
      <Flex gap={2}>
        <Icon icon={Board} size={18} />
        <Icon icon={MoveArrowRight} size={12} />
        <Icon icon={Team} size={16} />
      </Flex>
      <div style={{ marginRight: '8px', marginLeft: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </div>
      {helpLink && !hideHelpLink ? (
        <div>
          <ExternalLink href={helpLink} style={{ color: '#1f76c2' }}>
            Need help?
          </ExternalLink>
        </div>
      ) : null}
    </Flex>
  );
}
